import { useMutation } from "@tanstack/react-query";

import { useGraphQLClient } from "../graphql/client";
import {
  indexMetaobjectQuery,
  mediaMetaobjectQuery,
  metaobjectByHandleQuery,
  metaobjectImageQuery,
  metaobjectQuery,
  metaobjectTypeQuery,
} from "../graphql/metaobject-gql";
import { useCacheFirstRequest } from "../graphql/queryClient";
import type {
  IIndexMetaObjectQuery,
  IMediaMetaObjectQuery,
  IMetaObjectTypeQuery,
  IMetaobjectByHandleQuery,
  IMetaobjectImageQuery,
  IMetaobjectQuery,
} from "../interface/metaobject";
import { locale } from "../locales";
import { ToastType } from "../types/layout";
import type { IUseMetaObjectQuery } from "../types/queries";

export default function useMetaObjectQuery({ lang, openToast }: IUseMetaObjectQuery) {
  const request = useGraphQLClient();
  const cacheFirstRequest = useCacheFirstRequest();

  const metaObjectMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) => request<IMetaobjectQuery>(metaobjectQuery, variables),
    onError: (error) => {
      openToast({ message: locale(lang).error.server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  const metaObjectMediaMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) =>
      cacheFirstRequest<IMediaMetaObjectQuery>("metaobject-media", mediaMetaobjectQuery, variables),
    onError: (error) => {
      openToast({ message: locale(lang).error.server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  const metaObjectTypeMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) => request<IMetaObjectTypeQuery>(metaobjectTypeQuery, variables),
    onError: (error) => {
      openToast({ message: locale(lang).error.server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  const metaObjectImageMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) => request<IMetaobjectImageQuery>(metaobjectImageQuery, variables),
    onError: (error) => {
      openToast({ message: locale(lang).error.server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  const metaObjectHandleMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) =>
      cacheFirstRequest<IMetaobjectByHandleQuery>("metaobject-handle", metaobjectByHandleQuery, variables),
    onError: (error) => {
      openToast({ message: locale(lang).error.server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  /** index(홈 배너 등) 메타오브젝트 조회 */
  const metaObjectIndexMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) => request<IIndexMetaObjectQuery>(indexMetaobjectQuery, variables),
    onError: (error) => {
      openToast({ message: locale(lang).error.server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  return {
    fetchMetaObject: metaObjectMutation.mutateAsync,
    metaObjectData: metaObjectMutation.data,
    metaObjectLoading: metaObjectMutation.isPending,
    fetchMetaObjectMedia: metaObjectMediaMutation.mutateAsync,
    metaObjectMediaData: metaObjectMediaMutation.data,
    metaObjectMediaLoading: metaObjectMediaMutation.isPending,
    fetchMetaObjectType: metaObjectTypeMutation.mutateAsync,
    metaObjectTypeData: metaObjectTypeMutation.data,
    metaObjectTypeLoading: metaObjectTypeMutation.isPending,
    fetchMetaObjectImage: metaObjectImageMutation.mutateAsync,
    metaObjectImageData: metaObjectImageMutation.data,
    metaObjectImageLoading: metaObjectImageMutation.isPending,
    fetchMetaObjectHandle: metaObjectHandleMutation.mutateAsync,
    metaObjectHandleData: metaObjectHandleMutation.data,
    metaObjectHandleLoading: metaObjectHandleMutation.isPending,
    fetchMetaObjectIndex: metaObjectIndexMutation.mutateAsync,
    metaObjectIndexData: metaObjectIndexMutation.data,
    metaObjectIndexLoading: metaObjectIndexMutation.isPending,
  };
}
